/**
 * sessionReset — the single sign-out entry point.
 *
 * Sign-out used to call clearToken() and nothing else, so everything else
 * about the previous account survived in the tab: app.state devices / pubkeys
 * / servings / isDemo, the GlobalAccess class-level state, the cconfig and
 * billing-status caches, the device-backend registry and the polling loop. A
 * second account signing in in the same tab briefly saw the first account's
 * sites and, for up to five minutes, its billing interstitial (audit
 * 2026-10-15, M5).
 *
 * Called from an explicit sign-out (Header) and from a CONFIRMED session-token
 * 401/403 (authInterceptor's session-lost handler, registered by App). Never
 * call it for a transient error — those must keep prior data (CLAUDE.md,
 * "Device Data Loading & Resilience").
 */

import Configuration from '../constant/configuration';
import { clearToken } from './tokenStorage';
import GlobalAccess from './globalAccess';
import { invalidateCache } from './cconfigService';
import { clearBackends } from './deviceBackend';
import { invalidate as invalidateBillingStatus } from './billingService';

/**
 * The per-account slice of App state, at its boot values. App's constructor
 * spreads this, so a reset always lands on exactly what a fresh load starts
 * from. Returns fresh objects on every call.
 *
 * @returns {object}
 */
export function getSessionStateDefaults() {
  return {
    devices: {},
    devicesLoaded: false,
    // Set by GlobalAccess.getDevices once /customer answers; until then
    // isDemo is unknown and pages must not call the billing worker.
    customerLoaded: false,
    loadError: null,
    servings: {},
    pubkeys: {},
    refresh: false,
    isDemo: 0,
    untappd_token: '',
    bizDayStart: '00:00',
    enabledServingSizes: Configuration.defaultEnabledServingSizes,
  };
}

/**
 * State that only exists once a page has loaded something (servings progress,
 * cleaning history). Absent at boot, so it resets to undefined / empty.
 *
 * @param {object} prevState
 * @returns {object}
 */
function loadedOnlyStateReset(prevState) {
  return {
    isServingsLoading: false,
    servingsLoadingProgress: null,
    servingsMissingDevices: [],
    servingsRetryAttempted: false,
    servingsError: null,
    // A new version invalidates any memo keyed on it (Reports).
    servingsVersion: (prevState.servingsVersion || 0) + 1,
    cleaningAlerts: undefined,
    cleaningAlertsError: null,
    isCleaningAlertsLoading: false,
  };
}

// Every session cache except the token itself.
function resetAccountServices() {
  GlobalAccess.resetSessionState();
  invalidateCache();
  clearBackends();
  invalidateBillingStatus();
}

/**
 * Drop everything this tab knows about the current account — GlobalAccess
 * state and in-flight requests, the cconfig / billing / backend caches, the
 * polling loop and App's per-account state — WITHOUT touching the stored
 * token. Used directly when another tab signed in as a different account
 * (services/sessionSync.js): that tab set the token deliberately.
 *
 * @param {object|null} app          The App instance (null before boot).
 * @param {object} [extraState]      Merged into the same setState (e.g. the
 *                                   adopted ktok).
 * @param {Function} [onApplied]     setState callback, run once committed.
 * @returns {void}
 */
export function resetAccountState(app, extraState = {}, onApplied) {
  resetAccountServices();
  if (!app) return;

  // Stop the loop, but leave it restartable: startPollingIfNeeded starts a
  // new one on the next login (Login and Sites both call it).
  if (typeof app.stopPolling === 'function') app.stopPolling();

  const nextState = {
    ...getSessionStateDefaults(),
    ...loadedOnlyStateReset(app.state || {}),
    ...extraState,
  };
  if (onApplied) {
    app.setState(nextState, onApplied);
  } else {
    app.setState(nextState);
  }
}

/**
 * End the current session: clear the token and every session cache, stop the
 * polling loop, and return App's per-account state to its boot defaults.
 * Idempotent. Does not navigate — callers route to the login page.
 *
 * @param {object|null} app  The App instance; may be null before boot, in
 *                           which case only the token and caches are cleared.
 * @returns {void}
 */
export function resetSession(app) {
  clearToken();

  if (typeof document !== 'undefined') {
    // The next account's theme preference is loaded after it signs in; the
    // login page is always dark.
    document.documentElement.removeAttribute('data-theme');
  }

  resetAccountState(app, { ktok: null, theme: 'dark' });
}
